import React, {FunctionComponent, useEffect, useState} from 'react';
import {Modal} from 'react-bootstrap';
import {RouteComponentProps, useHistory, useParams} from 'react-router-dom';
import {useForm} from "react-hook-form";
import {Trans, useTranslation} from 'react-i18next';

import './EditModel.scss';
import {Breadcrumbs, Button, ErrorMessage, FormInput, FormSelect, Loader, Page, PageHeader} from 'components';
import {IDevice, IDeviceModel, IManufacturer} from 'interfaces';
import {ErrorType, LoadState} from 'enums';
import {DeviceInfoService, parseAxiosError} from 'services';
import {useCancelablePromise} from 'hooks';

type IModelForm = {
    manufacturerId: string;
    model: string;
}

export const EditModel: FunctionComponent<{}> = (props) => {
    const { cancelablePromise } = useCancelablePromise();
    const form = useForm<IModelForm>({mode: 'onTouched'});
    const { t, i18n } = useTranslation();
    const history = useHistory();
    let urlParams = useParams<{ id: string }>();
    const [loadState, setLoadState] = useState(LoadState.Loading);
    const [saveState, setSaveState] = useState(LoadState.Initial);
    const [deleteState, setDeleteState] = useState(LoadState.Initial);
    const [loadError, setLoadError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const isAPICall = saveState === LoadState.Loading || deleteState === LoadState.Loading;
    // Watch the manufacturer field so we can update the model when it changes
    // The type assertions are because react-hook-form's type validation isn't working correctly,
    // and we want to get a compile error if a field is ever renamed.
    const field_manufacturerId: keyof IModelForm = 'manufacturerId';
    const field_model: keyof IModelForm = 'model';
    const manufacturerId = form.watch(field_manufacturerId, undefined);

    const isNew = urlParams.id === 'new';
    let id: number;
    if (!isNew) {
        id = parseInt(urlParams.id, 10);
    }

    const mapModelToForm = (f: IDeviceModel): IModelForm => {
        return {
            // Browser selects and react-hook-form only support strings, so need to map.
            manufacturerId: f.manufacturer_id + '',
            model: f.name
        }
    }

    const mapFormToModel = (formData: IModelForm): IDeviceModel => {
        // Note: model id will be undefined for existing model versions,
        // because browsers don't submit the values of disabled fields.
        return {
            id: id,
            manufacturer_id: parseInt(formData.manufacturerId, 10),
            name: formData.model
        };
    }

    // Load the models and manufacturers
    useEffect(() => {
        let promises = [cancelablePromise(DeviceInfoService.getManufacturerMap())];
        if (!isNew) {
            promises.push(cancelablePromise(DeviceInfoService.getModel(id)));
        }
        Promise.all(promises)
            .then(([deviceInfo, mod]) => {
                if (isNew) {
                    const mans = DeviceInfoService.manufacturers;
                    // Vendors are only going to have 1 manufacturer, so autopopulate our values if we only have 1.
                    if (mans.length === 1) {
                        const formVals: Partial<IModelForm> = {};
                        const man = mans[0];
                        formVals.manufacturerId = man.id + '';
                        formVals.model = '';
                        form.reset(formVals);
                    }
                } else { 
                    form.reset(mapModelToForm(mod));
                }
                setLoadState(LoadState.Success);
            })
            .catch((err) => {
                setLoadError(parseAxiosError(err));
                setLoadState(LoadState.Error);
            });
    }, []);

    const onManufacturerChange = () => {
        // Need to clear the model field when the manufacturer changes.
        form.setValue(field_model, '');
    }

    let isSubmitting = false;
    const onSubmit = (formData: IModelForm) => {
        console.log(formData);
        if (isSubmitting || loadState !== LoadState.Success || saveState === LoadState.Loading) return;
        // "setState" is async, so set a var immediately to prevent possible double submit.
        isSubmitting = true;
        setSaveError('');
        setSaveState(LoadState.Loading);

        const modelFromForm = mapFormToModel(formData);

        const p = cancelablePromise(DeviceInfoService.createModel(modelFromForm));
        p.then((mod) => {
                // Update the form with the returned object from the server.
                form.reset(mapModelToForm(mod));
                setSaveState(LoadState.Success);
                history.push('/models');
            })
            .catch((err) => {
                console.log('ERROR saving: ', err);
                setSaveState(LoadState.Error);
                setSaveError(parseAxiosError(err));
            })
    }

    const onCancel = () => {
        // No need to do anything, just go back to models
        history.push('/models');
    }

    const onOpenDeleteModal = () => {
        // Clear any errors
        if (deleteState === LoadState.Error) {
            setDeleteState(LoadState.Initial);
            setDeleteError('');
        }
        setShowDeleteModal(true);
    }

    const onDelete = () => {
        if (isSubmitting) return;
        if (loadState !== LoadState.Success) return;
        if (saveState === LoadState.Loading) return;
        // "setState" is async, so set a var immediately to prevent possible double submit.
        isSubmitting = true;
        setDeleteError('');
        setDeleteState(LoadState.Loading);

        cancelablePromise(DeviceInfoService.deleteModel(id))
            .then(() => {
                history.push('/models');
            })
            .catch((err) => {
                console.log('ERROR deleting: ', err);
                setDeleteState(LoadState.Error);
                setDeleteError(parseAxiosError(err));
            })
    }

    const requestDeleteModalClose = () => {
        // Ignore close requests if we're currently deleting
        if (deleteState !== LoadState.Loading) {
            setShowDeleteModal(false);
        }
    }

    const breadcrumbs = (
        <Breadcrumbs crumbs={[
            {
                label: t('modelsList.title'),
                url: '/models',
            },
        ]} 
        dataCy="edit_breadcrumb"
        />
    );

    let content = undefined;
    switch (loadState) {
        case LoadState.Initial:
        case LoadState.Loading:
            content = <Loader />;
            break;
        case LoadState.Success: {
            
            content = (<>
                {saveState === LoadState.Error && (
                    <ErrorMessage type={ErrorType.Generic} message={saveError} />
                )}
                <p>
                    Please enter your model.
                </p>
                <form onSubmit={form.handleSubmit(onSubmit)} data-cy="edit_form">
                    <div className="row">
                        <div className="col">
                            <FormSelect
                                disabled={!isNew || isAPICall}
                                form={form}
                                getOptionLabel={man => man.name}
                                getOptionValue={man => man.id + ''}
                                label={t('editModel.fields.manufacturer')}
                                name="manufacturerId"
                                onChange={onManufacturerChange}
                                options={DeviceInfoService.manufacturers}
                                registerOptions={{required: true}}
                                dataCy="edit_manufacturerDropdown"
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <FormInput
                                disabled={isAPICall}
                                form={form}
                                label={t('editModel.fields.model')}
                                maxLength={128}
                                name="model"
                                registerOptions={{required: true}}
                                dataCy="edit_modelInput"
                            />
                        </div>
                    </div>
                    {loadState === LoadState.Success && (
                        <div className="formFooter">
                            {isNew && (
                                <Button theme="text" type="button" disabled={isAPICall} onClick={onCancel} dataCy="edit_cancelButton">
                                    {t('button.cancel')}
                                </Button>
                            )}
                            {!isNew && (
                                <Button theme="text" type="button" disabled={isAPICall} onClick={onOpenDeleteModal} dataCy="edit_deleteButton">
                                    {t('button.delete')}
                                </Button>
                            )}
                            <Button theme="red" type="submit" disabled={isAPICall} dataCy="edit_submitButton">
                                {saveState === LoadState.Loading
                                    ? t('button.saving')
                                    : t('button.save')}
                            </Button>
                        </div>
                    )}
                </form>
            </>);
            break;
        }
        case LoadState.Error:
            content = <ErrorMessage type={ErrorType.Generic} message={loadError} dataCy="edit_loadError"/>
            break;
    }

    return (
        <Page>
            <PageHeader
                top={breadcrumbs}
                title={isNew ? t('editModel.newTitle') : t('editModel.title')}
            />
            {content}
            <Modal show={showDeleteModal} onHide={requestDeleteModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('editModel.deleteModal.header')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteState === LoadState.Error && (
                        <ErrorMessage type={ErrorType.Generic} message={deleteError} />
                    )}
                    {t('editModel.deleteModal.body')}
                </Modal.Body>
                <Modal.Footer>
                    <Button theme="text" onClick={requestDeleteModalClose} disabled={isAPICall} dataCy="deleteModal_cancelButton">
                        {t('button.cancel')}
                    </Button>
                    <Button theme="red" onClick={onDelete} disabled={isAPICall} dataCy="deleteModal_confirmButton">
                        {deleteState === LoadState.Loading
                            ? t('button.deleting')
                            : t('button.delete')
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
        </Page>
    );
}